import { Contact } from './contact';
import { Nullable } from 'src/core/common/domain/types/types';
import { TaskTaskType, TaskTaskTypePrimitives } from './task-task-type';
import { TaskActions } from './task-actions';

export type ServiceAddressPrimitives = {
  id: string;
  name: string;
  recipient_emails: Array<{ email: string; full_name: Nullable<string> }>;
  create_task_task_types: Array<TaskTaskTypePrimitives>;
  // Optional to stay backward-compatible with records stored before it existed. Missing or null means the
  // service address does not say, so its tasks fall back to the top-level actions; an empty list grants none.
  available_actions?: Nullable<Array<TaskActions>>;
};

export class ServiceAddress {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly recipientEmails: Array<Contact> = [],
    public readonly createTaskTaskTypes: Array<TaskTaskType> = [],
    public readonly availableActions: Nullable<Array<TaskActions>> = null
  ) {}

  static fromPrimitives(primitives: ServiceAddressPrimitives) {
    const recipientEmails =
      primitives.recipient_emails?.map((r) => {
        return { email: r.email.trim().toLowerCase(), fullName: r.full_name ?? null };
      }) ?? [];
    const createTaskTaskTypes = primitives.create_task_task_types
      ? primitives.create_task_task_types.map((o) => TaskTaskType.fromPrimitives(o))
      : [];

    return new this(
      primitives.id,
      primitives.name,
      recipientEmails,
      createTaskTaskTypes,
      primitives.available_actions ?? null
    );
  }

  toPrimitives(): ServiceAddressPrimitives {
    return {
      id: this.id,
      name: this.name,
      recipient_emails: this.recipientEmails.map((r) => ({
        email: r.email.trim().toLowerCase(),
        full_name: r.fullName ?? null,
      })),
      create_task_task_types: this.createTaskTaskTypes.map((t) => t.toPrimitives()),
      available_actions: this.availableActions,
    };
  }

  addEmails(emails: Array<string>): boolean {
    const existing = new Set(this.recipientEmails.map((c) => c.email.trim().toLowerCase()));
    let added = false;

    for (const email of emails) {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) {
        continue;
      }

      if (!existing.has(normalizedEmail)) {
        this.recipientEmails.push({ email: normalizedEmail, fullName: null });
        existing.add(normalizedEmail);
        added = true;
      }
    }

    return added;
  }
}
